import React from 'react';
import { Link } from 'react-router-dom';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-bg">
      <SiteNav />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="font-display text-4xl font-bold text-textPrimary mb-2">Terms of Service</h1>
        <p className="text-textSecondary mb-8">Last updated: June 2026</p>

        <div className="prose prose-invert prose-lg max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">1. Agreement to Terms</h2>
            <p className="text-textSecondary leading-relaxed">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and Bankably, Inc. ("Bankably," "we," "us," or "our"). By accessing, creating an account on, or using our website, mobile application, and related services (collectively, the "Services"), you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p className="text-textSecondary leading-relaxed mt-4">
              If you do not agree to these Terms, you may not access or use the Services. We reserve the right to modify these Terms at any time. Your continued use of the Services following notice of such modifications constitutes your acceptance of the updated Terms.
            </p>
          </section>

          {/* Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">2. Eligibility and Account Registration</h2>
            <p className="text-textSecondary leading-relaxed">
              To use our Services, you must:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Be at least 18 years of age</li>
              <li>Be a legal resident of the United States or eligible jurisdiction</li>
              <li>Not be prohibited by law from using the Services</li>
              <li>Not be on any government sanctions or blocked persons list</li>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the confidentiality of your login credentials</li>
              <li>Pass our Know Your Customer (KYC) and identity verification procedures</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              You are responsible for all activities that occur under your account. You agree to notify us immediately of any unauthorized access or suspicious activity.
            </p>
          </section>

          {/* Account Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">3. User Responsibilities</h2>
            <p className="text-textSecondary leading-relaxed">
              You agree to:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Use the Services in compliance with all applicable laws and regulations</li>
              <li>Not engage in fraudulent, deceptive, or illegal activities</li>
              <li>Not attempt to breach security features or gain unauthorized access</li>
              <li>Not use the Services for money laundering, terrorist financing, or sanctions evasion</li>
              <li>Not reverse engineer, disassemble, or decompile our platform</li>
              <li>Not use automated tools or bots to access the Services without permission</li>
              <li>Maintain accurate and up-to-date account information</li>
              <li>Notify us promptly of any account compromises or security breaches</li>
            </ul>
          </section>

          {/* Financial Services */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">4. Financial Services and Transactions</h2>

            <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">4.1 No Investment Advice</h3>
            <p className="text-textSecondary leading-relaxed">
              The Services are provided for informational purposes only. Nothing on our platform constitutes investment advice, financial advice, or a recommendation to buy or sell any security. You are responsible for conducting your own research and making your own investment decisions.
            </p>

            <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">4.2 Transaction Accuracy</h3>
            <p className="text-textSecondary leading-relaxed">
              You are solely responsible for the accuracy of transaction details, including recipient information and amounts. Bankably is not liable for losses resulting from errors in your transaction details.
            </p>

            <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">4.3 Fees and Charges</h3>
            <p className="text-textSecondary leading-relaxed">
              We reserve the right to charge fees for our Services as disclosed in your account or on our website. You authorize us to deduct applicable fees from your account. Any changes to fees will be communicated to you in advance.
            </p>

            <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">4.4 Minimum Balance and Inactivity</h3>
            <p className="text-textSecondary leading-relaxed">
              Accounts may be subject to minimum balance requirements or inactivity fees as outlined in our fee schedule. Accounts inactive for 12 months may be suspended or closed.
            </p>
          </section>

          {/* Limitations of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">5. Limitation of Liability</h2>
            <p className="text-textSecondary leading-relaxed">
              TO THE FULLEST EXTENT PERMITTED BY LAW, BANKABLY AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Service interruptions or technical failures beyond our reasonable control</li>
              <li>Unauthorized access or data breaches due to your negligence</li>
              <li>Errors or omissions in transaction processing</li>
              <li>Third-party actions or services linked through our platform</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              Our total liability shall not exceed the amount you deposited in your account or $100, whichever is greater.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">6. Disclaimer of Warranties</h2>
            <p className="text-textSecondary leading-relaxed">
              THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">7. Indemnification</h2>
            <p className="text-textSecondary leading-relaxed">
              You agree to indemnify, defend, and hold harmless Bankably and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Your use of the Services in violation of these Terms</li>
              <li>Your violation of any applicable law or regulation</li>
              <li>Your infringement of any third-party rights</li>
              <li>Any fraudulent or illegal activity conducted through your account</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">8. Account Termination</h2>
            <p className="text-textSecondary leading-relaxed">
              Bankably may terminate or suspend your account and access to the Services at any time, with or without cause and without notice, including if:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>You violate these Terms or any applicable law</li>
              <li>We detect suspicious or fraudulent activity</li>
              <li>You provide false or misleading information</li>
              <li>We are required to do so by law or regulation</li>
              <li>Your account presents elevated compliance or risk concerns</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              Upon termination, your right to use the Services immediately ceases. Any funds in your account will be returned to you in accordance with applicable law.
            </p>
          </section>

          {/* Regulatory Compliance */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">9. Regulatory Compliance</h2>
            <p className="text-textSecondary leading-relaxed">
              Bankably operates in compliance with applicable financial regulations, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li>Bank Secrecy Act (BSA) and Anti-Money Laundering (AML) requirements</li>
              <li>Know Your Customer (KYC) and Customer Due Diligence (CDD) rules</li>
              <li>Office of Foreign Assets Control (OFAC) sanctions screening</li>
              <li>Truth in Lending Act (TILA) and Regulation E</li>
              <li>Fair Credit Reporting Act (FCRA)</li>
              <li>Securities laws and regulations</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              You acknowledge that Bankably is required to collect and verify your personal information to comply with these regulations.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">10. Dispute Resolution</h2>
            <p className="text-textSecondary leading-relaxed">
              Any disputes arising from these Terms or your use of the Services shall be resolved through:
            </p>
            <ul className="list-disc list-inside text-textSecondary space-y-2 mt-3">
              <li><strong className="text-textPrimary">Informal Resolution:</strong> First, you agree to contact us and attempt to resolve the dispute informally</li>
              <li><strong className="text-textPrimary">Arbitration:</strong> If not resolved, disputes shall be resolved through binding arbitration under the AAA Commercial Arbitration Rules</li>
              <li><strong className="text-textPrimary">Governing Law:</strong> These Terms are governed by the laws of New York, without regard to its conflict of law principles</li>
            </ul>
            <p className="text-textSecondary leading-relaxed mt-4">
              You waive your right to pursue disputes in court and agree to arbitration as the exclusive remedy.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">11. Third-Party Links and Services</h2>
            <p className="text-textSecondary leading-relaxed">
              Our platform may contain links to third-party websites and services. Bankably is not responsible for the content, accuracy, or practices of these third parties. Your use of third-party services is at your own risk and subject to their terms and conditions. We encourage you to review their privacy policies and terms of service.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">12. Intellectual Property Rights</h2>
            <p className="text-textSecondary leading-relaxed">
              All content, features, and functionality of the Services (including text, graphics, logos, and software) are owned by Bankably, its licensors, or other providers and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or transmit any content without our written permission.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">13. Contact Us</h2>
            <p className="text-textSecondary leading-relaxed">
              For questions or concerns regarding these Terms of Service, please contact us at:
            </p>
            <div className="bg-surface1 border border-border p-6 rounded-lg mt-4 text-textSecondary">
              <p><strong className="text-textPrimary">Bankably Legal Team</strong></p>
              <p>Email: legal@bankably.co</p>
              <p>Address: 123 Financial Street, New York, NY 10001, USA</p>
            </div>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">14. Severability</h2>
            <p className="text-textSecondary leading-relaxed">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect, and the invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
            </p>
          </section>

          {/* Entire Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-textPrimary mb-4">15. Entire Agreement</h2>
            <p className="text-textSecondary leading-relaxed">
              These Terms of Service, together with our Privacy Policy and any other agreements referenced herein, constitute the entire agreement between you and Bankably regarding your use of the Services and supersede all prior and contemporaneous agreements, understandings, and negotiations.
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-16 pt-8 border-t border-border flex gap-6">
          <Link to="/" className="text-gold hover:text-goldLight font-semibold">
            ← Back to Home
          </Link>
          <Link to="/privacy-policy" className="text-gold hover:text-goldLight font-semibold">
            Privacy Policy →
          </Link>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
